import type { IClient } from '../type';

/**
 * How long ago, in the shortest form that is still honest.
 *
 * The tiles above the table answer "how old is this account's oldest key" and
 * "when did we last mint one" — questions a date alone makes the reader do
 * arithmetic for. The exact timestamp is still one row away, in the table and
 * in the credentials modal, so nothing is lost by rounding here.
 */
export const ago = (iso?: string): string => {
	if (!iso) return '--';
	const then = new Date(iso).getTime();
	if (Number.isNaN(then)) return '--';

	const days = Math.floor((Date.now() - then) / 86400000);
	if (days <= 0) return 'today';
	if (days === 1) return '1d';
	if (days < 30) return `${days}d`;

	const months = Math.floor(days / 30);
	if (months < 12) return `${months}mo`;
	return `${Math.floor(months / 12)}y`;
};

/** Newest first — the key someone just minted is the one they came to find. */
export const byNewest = (keys: IClient[]): IClient[] =>
	[...keys].sort(
		(a, b) =>
			new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime()
	);

/**
 * Name and client ID, case-insensitive. The secret is deliberately NOT
 * searchable: matching on it would mean a shoulder-surfer could confirm a
 * secret they already half-know without ever opening the modal.
 */
export const search = (keys: IClient[], term: string): IClient[] => {
	const q = term.trim().toLowerCase();
	if (!q) return keys;
	return keys.filter(
		k =>
			(k.name ?? '').toLowerCase().includes(q) ||
			(k.clientId ?? '').toLowerCase().includes(q)
	);
};

/**
 * The three facts the strip reports. `lastUpdated` only counts a key whose
 * `updatedAt` differs from its `createdAt` — otherwise every freshly minted
 * key would read as "just renamed", which is not a thing that happened.
 */
export const summarise = (keys: IClient[]) => {
	const sorted = byNewest(keys);
	const newest = sorted[0];
	const oldest = sorted[sorted.length - 1];
	const renamed = keys
		.filter(k => k.updatedAt && k.updatedAt !== k.createdAt)
		.sort(
			(a, b) =>
				new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
		)[0];

	return { count: keys.length, newest, oldest, renamed };
};
